import React from 'react';
import { i18n } from 'config/locales';

import { ForeignBusinessFamilyItemModel } from 'forms/sections/foreign/business/family/types';
import ValidationElement, {
  ValidationElementProps,
} from 'components/Form/ValidationElement/ValidationElement';
import {
  CommonOnUpdate, CommonScrollIntoView, CommonOnError, CommonRequired
} from 'types/props';
import { ErrorCode } from '@redux/modules/application/reducers/error';
import Field from 'components/Form/Field';
import Name from 'components/Form/Name';
import Country from 'components/Form/Country';
import DateControl from 'components/Form/DateControl';
import Textarea from 'components/Form/Textarea';
import Text from 'components/Form/Text';
import { isPVQForm } from 'helpers/form';
import RadioGroup from 'components/Form/RadioGroup';
import PVQName from 'components/Form/PVQName';
import Radio from 'components/Form/Radio';
import { foreignBusinessAffairsFamilyOptions } from 'constants/enums/foreignBusinessOptions';

interface Props extends Partial<ForeignBusinessFamilyItemModel>, ValidationElementProps {
  onUpdate?: CommonOnUpdate
  scrollIntoView?: CommonScrollIntoView
  onError?: CommonOnError
  required?: CommonRequired
  formType?: string
}

export default class FamilyItem extends ValidationElement<Props> {
  constructor(props) {
    super(props);
    this.updateCountryFocused = this.updateCountryFocused.bind(this);
  }

  handleNameError = (value, arr: ErrorCode[]) => {
    const arr2 = arr.map((err) => ({
      ...err,
      code: err.code.includes('required')
        ? `name${err.code.substring(err.code.lastIndexOf('.'))}`
        : err.code,
    }));
    return this.props.onError(value, arr2);
  };

  update = (queue: Partial<ForeignBusinessFamilyItemModel>) => {
    this.props.onUpdate({
      Name: this.props.Name,
      Agency: this.props.Agency,
      Country: this.props.Country,
      Date: this.props.Date,
      Circumstances: this.props.Circumstances,
      CountryFocused: this.props.CountryFocused,
      WhoWasAsked: this.props.WhoWasAsked, // PVQ Only
      FamilyName: this.props.FamilyName, // PVQ Only
      ...queue,
    });
  };

  updateName = (values: ForeignBusinessFamilyItemModel['Name']) => {
    this.update({
      Name: values,
    });
  };

  updateAgency = (values: ForeignBusinessFamilyItemModel['Agency']) => {
    this.update({
      Agency: values,
    });
  };

  updateCountry = (values: ForeignBusinessFamilyItemModel['Country']) => {
    this.update({
      Country: values,
    });
  };

  updateDate = (values: ForeignBusinessFamilyItemModel['Date']) => {
    this.update({
      Date: values,
    });
  };

  updateCircumstances = (values: ForeignBusinessFamilyItemModel['Circumstances']) => {
    this.update({
      Circumstances: values,
    });
  };

  updateCountryFocused(focus: boolean) {
    this.update({
      CountryFocused: focus,
    });
  }

  updateWhoWasAsked = (value: ForeignBusinessFamilyItemModel['WhoWasAsked']) => {
    this.update({
      WhoWasAsked: value,
      FamilyName: value?.value !== 'Myself' ? this.props.FamilyName : {},
    });
  };

  updateFamilyName = (values: ForeignBusinessFamilyItemModel['FamilyName']) => {
    this.update({
      FamilyName: values,
    });
  };

  render() {
    const attribute = `List.items[${this.props.$index}].Item`;
    const isPVQ = isPVQForm(this.props.formType);

    return isPVQ ? (
      <div className="family-item">
        <Field
          title={i18n.t('foreign.pvq.business.family.heading.whoWasAsked')}
          className="who-was-asked"
          adjustFor="p"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.WhoWasAsked`}
        >
          <RadioGroup
            className="who-was-asked option-list option-list-vertical"
            required={this.props.required}
            onError={this.props.onError}
            selectedValue={this.props.WhoWasAsked?.value}
            attribute={`${attribute}.WhoWasAsked`}
          >
            {foreignBusinessAffairsFamilyOptions.map((option) => (
              <Radio
                key={option}
                name={option}
                label={option}
                value={option}
                className={option}
                onUpdate={this.updateWhoWasAsked}
                onError={this.props.onError}
              />
            ))}
          </RadioGroup>
        </Field>

        {this.props.WhoWasAsked?.value && this.props.WhoWasAsked?.value !== 'Myself'
          && (
            <Field
              title={i18n.t('foreign.pvq.business.family.heading.familyName')}
              optional
              filterErrors={Name.requiredErrorsOnly}
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.FamilyName`}
            >
              <PVQName
                {...this.props.FamilyName}
                name="FamilyName"
                attribute={`${attribute}.FamilyName`}
                aria-label={i18n.t('foreign.pvq.business.family.heading.familyName')}
                onUpdate={this.updateFamilyName}
                onError={this.handleNameError}
                className="family-name"
                required={this.props.required}
                scrollIntoView={this.props.scrollIntoView}
                showLastFirstMiddleNameOrder
              />
            </Field>
          )}

        <Field
          title={i18n.t('foreign.pvq.business.family.heading.name')}
          optional
          filterErrors={Name.requiredErrorsOnly}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Name`}
        >
          <PVQName
            {...this.props.Name}
            name="Name"
            attribute={`${attribute}.Name`}
            aria-label={i18n.t('foreign.pvq.business.family.heading.name')}
            onUpdate={this.updateName}
            onError={this.handleNameError}
            className="name"
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            showLastFirstMiddleNameOrder
          />
        </Field>

        <Field
          title={i18n.t('foreign.pvq.business.family.heading.country')}
          attribute={`${attribute}.Country`}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Country
            name="Country"
            attribute={`${attribute}.Country`}
            {...this.props.Country}
            excludeUnitedStates
            onUpdate={this.updateCountry}
            updateCountryFocused={this.updateCountryFocused}
            onError={this.props.onError}
            className="family-country"
            required={this.props.required}
          />
        </Field>

        <Field
          title={i18n.t('foreign.pvq.business.family.heading.agency')}
          attribute={`${attribute}.Agency`}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Text
            name="Agency"
            {...this.props.Agency}
            attribute={`${attribute}.Agency`}
            onUpdate={this.updateAgency}
            onError={this.props.onError}
            className="family-agency"
            required={this.props.required}
          />
        </Field>

        <Field
          title={i18n.t('foreign.pvq.business.family.heading.circumstances')}
          attribute={`${attribute}.Circumstances`}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Textarea
            {...this.props.Circumstances}
            name="Circumstances"
            attribute={`${attribute}.Circumstances`}
            onUpdate={this.updateCircumstances}
            onError={this.props.onError}
            className="family-circumstances"
            required={this.props.required}
          />
        </Field>

        <Field
          title={i18n.t('foreign.pvq.business.family.heading.date')}
          attribute={`${attribute}.Date`}
          help="foreign.pvq.business.family.help.date"
          adjustFor="datecontrol"
          scrollIntoView={this.props.scrollIntoView}
        >
          <DateControl
            {...this.props.Date}
            name="Date"
            attribute={`${attribute}.Date`}
            onUpdate={this.updateDate}
            onError={this.props.onError}
            className="family-date"
            required={this.props.required}
            hideDay
          />
        </Field>
      </div>
    ) : (
      <div className="family-item">
        <Field
          title={i18n.t('foreign.business.family.heading.name')}
          optional
          filterErrors={Name.requiredErrorsOnly}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Name
            name="Name"
            attribute={`${attribute}.Name`}
            aria-label="Provide the name of the government official"
            {...this.props.Name}
            onUpdate={this.updateName}
            onError={this.handleNameError}
            className="family-name"
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
          />
        </Field>

        <Field
          title={i18n.t('foreign.business.family.heading.agency')}
          attribute={`${attribute}.Agency`}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Text
            name="Agency"
            {...this.props.Agency}
            attribute={`${attribute}.Agency`}
            onUpdate={this.updateAgency}
            onError={this.props.onError}
            className="family-agency"
            required={this.props.required}
          />
        </Field>

        <Field
          title={i18n.t('foreign.business.family.heading.country')}
          attribute={`${attribute}.Country`}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Country
            name="Country"
            attribute={`${attribute}.Country`}
            {...this.props.Country}
            excludeUnitedStates
            onUpdate={this.updateCountry}
            updateCountryFocused={this.updateCountryFocused}
            onError={this.props.onError}
            className="family-country"
            required={this.props.required}
          />
        </Field>

        <Field
          title={i18n.t('foreign.business.family.heading.date')}
          attribute={`${attribute}.Date`}
          help="foreign.business.family.help.date"
          adjustFor="datecontrol"
          scrollIntoView={this.props.scrollIntoView}
        >
          <DateControl
            name="Date"
            attribute={`${attribute}.Date`}
            {...this.props.Date}
            onUpdate={this.updateDate}
            onError={this.props.onError}
            className="family-date"
            required={this.props.required}
            hideDay
          />
        </Field>

        <Field
          title={i18n.t('foreign.business.family.heading.circumstances')}
          attribute={`${attribute}.Circumstances`}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Textarea
            name="Circumstances"
            attribute={`${attribute}.Circumstances`}
            {...this.props.Circumstances}
            onUpdate={this.updateCircumstances}
            onError={this.props.onError}
            className="family-circumstances"
            required={this.props.required}
          />
        </Field>
      </div>
    );
  }
}

FamilyItem.defaultProps = {
  onUpdate: (queue) => { },
  onError: (value, arr) => arr,
  CountryFocused: false,
  WhoWasAsked: {}, // PVQ Only
  FamilyName: {}, // PVQ Only
};
